import { graphql } from 'gatsby'
import React, { useMemo } from 'react'

import {
  FooterCompanyMenuFragmentFragment,
  FooterHelpMenuFragmentFragment,
  FooterQuestionMenuFragmentFragment,
  FooterResourcesMenuFragmentFragment,
} from '../../../../graphql/gatsby'
import useSiteSettings from '../../../../hooks/useSiteSettings'
import { useSanityRoleAndContextBasedContent } from '../../../../lib/sanity'
import { isNotNullAnd } from '../../../../utils/collectionTools'
import { FooterLinksGroup } from '../FooterLinksGroup'

type CompanyMenuEntry = NonNullable<
  NonNullable<FooterCompanyMenuFragmentFragment['footerCompanyMenu']>[number]
>

type HelpMenuEntry = NonNullable<
  NonNullable<FooterHelpMenuFragmentFragment['footerHelpMenu']>[number]
>

type QuestionMenuEntry = NonNullable<
  NonNullable<FooterQuestionMenuFragmentFragment['footerQuestionMenu']>[number]
>

type ResourcesMenuEntry = NonNullable<
  NonNullable<FooterResourcesMenuFragmentFragment['footerResourcesMenu']>[number]
>

export type FooterMenuEntry =
  | CompanyMenuEntry
  | HelpMenuEntry
  | QuestionMenuEntry
  | ResourcesMenuEntry

export interface FooterLinksGroupDetails {
  heading?: string
  id: string
  subHeading?: string
  links: FooterMenuEntry[]
}

export const FooterLinks: React.FC = () => {
  const siteSettings = useSiteSettings()
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const menuEntries = useMemo(() => {
    const {
      footerCompanyMenu: companyMenuEntries,
      footerHelpMenu: helpMenuEntries,
      footerQuestionMenu: questionMenuEntries,
      footerResourcesMenu: resourcesMenuEntries,
    } = siteSettings || {}
    return {
      companyLinks: companyMenuEntries?.filter(isNotNullAnd(shouldDisplayContent)) ?? [],
      helpLinks: helpMenuEntries?.filter(isNotNullAnd(shouldDisplayContent)) ?? [],
      questionLinks: questionMenuEntries?.filter(isNotNullAnd(shouldDisplayContent)) ?? [],
      resourcesLinks: resourcesMenuEntries?.filter(isNotNullAnd(shouldDisplayContent)) ?? [],
    }
  }, [shouldDisplayContent, siteSettings])

  const companySection: FooterLinksGroupDetails = {
    heading: 'Company',
    id: 'company',
    links: menuEntries.companyLinks,
  }
  const helpSection: FooterLinksGroupDetails = {
    heading: 'Contact',
    id: 'help',
    links: menuEntries.helpLinks,
  }
  const resourcesSection: FooterLinksGroupDetails = {
    heading: 'Resources',
    id: 'resources',
    links: menuEntries.resourcesLinks,
  }

  return (
    <div
      className={
        'grid grid-cols-1 sm:grid-cols-[1.3fr_1fr_1fr] gap-5 md:gap-8 xl:border-r xl:border-white'
      }
    >
      {helpSection.links.length > 0 && (
        <FooterLinksGroup
          heading={helpSection.heading}
          links={helpSection.links}
          section="Contact Information"
        />
      )}

      {companySection.links.length > 0 && (
        <FooterLinksGroup
          heading={companySection.heading}
          links={companySection.links}
          section="Company Links"
        />
      )}

      {resourcesSection.links.length > 0 && (
        <FooterLinksGroup
          heading={resourcesSection.heading}
          links={resourcesSection.links}
          section="Resources"
        />
      )}
    </div>
  )
}

export const footerCompanyMenu = graphql`
  fragment footerCompanyMenuFragment on SanitySiteSettings {
    footerCompanyMenu {
      name
      link {
        ...LinkFragment
      }
      roleBasedContent {
        enabled
        roles
      }
      contextBasedContent {
        enabled
        contexts
      }
    }
  }
`

export const footerHelpMenu = graphql`
  fragment footerHelpMenuFragment on SanitySiteSettings {
    footerHelpMenu {
      name
      link {
        ...LinkFragment
      }
      roleBasedContent {
        enabled
        roles
      }
      contextBasedContent {
        enabled
        contexts
      }
    }
  }
`

export const footerQuestionMenu = graphql`
  fragment footerQuestionMenuFragment on SanitySiteSettings {
    footerQuestionMenu {
      name
      link {
        ...LinkFragment
      }
      roleBasedContent {
        enabled
        roles
      }
      contextBasedContent {
        enabled
        contexts
      }
    }
  }
`

export const footerResourcesMenu = graphql`
  fragment footerResourcesMenuFragment on SanitySiteSettings {
    footerResourcesMenu {
      name
      link {
        ...LinkFragment
      }
      roleBasedContent {
        enabled
        roles
      }
      contextBasedContent {
        enabled
        contexts
      }
    }
  }
`
